import React, { useContext, useEffect } from 'react';

import { TrackEventContext } from 'common/containers/EventContainer';
import SpekitLandingHelmet from 'common/helmets/SpekitLandingHelmet';
import LandingFooterCTA from 'common/LandingFooterCTA';
import LazyLoadedImage from 'common/LazyLoadedImage';
import UppercaseHeader from 'common/UppercaseHeader';
import LandingCaseStudyHeadingText from 'common/www/LandingCaseStudyHeadingText';

import SpekitWordmark from 'img/landing/spekit-wordmark.svg';

import 'css/components/www/_LandingCustomersPage.scss';

const SpekitLanding = () => {
  const trackEvent = useContext(TrackEventContext);

  useEffect(() => {
    trackEvent('Viewed Spekit Case Study');
  }, [trackEvent]);

  return (
    <div className="landingSpekit landingCustomersPage">
      <SpekitLandingHelmet />
      <div className="fullWidthContainer">
        <div className="header">
          <LandingCaseStudyHeadingText title="Why Spekit relies on Canny to make data-backed product decisions" />
          <div className="headerVideo">
            <iframe
              width="560"
              height="315"
              src="https://www.youtube.com/embed/2jygnSK-EH8"
              title="YouTube video player"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen></iframe>
          </div>
        </div>
      </div>
      <div className="mainContent">
        <div className="mainContentText">
          <div className="left">
            <p>
              Spekit helps sales reps find the right content to close deals. This AI-powered
              sales-enablement platform has helped 75+ customers so far. Andrea Park is a business
              operations manager, which means she enables the entire team with the best tools. Canny
              was one of the first tools she chose to help her improve Spekit's operations.
            </p>
            <h2 className="sectionTitle">Problem</h2>
            <p>
              Andrea owns the entire feedback loop at Spekit. This means she ensures the smooth flow
              of feedback between different teams: product, sales, engineering, and others. If the
              feedback loop breaks at any point, Andrea finds and fixes it.
            </p>
            <p>Andrea started with having too much data but considers it a good problem.</p>
            <p className="quote">
              "One problem we don't have is the amount of data. But lots of data means we need to
              sort, organize, associate it with revenue, and more. The biggest gap prior to Canny
              was figuring out priorities."
            </p>
            <p>
              Before Canny, Spekit used Salesforce to manage feedback. However, the engineering team
              didn't use this tool, so they were missing lots of insights and felt unheard.
            </p>
            <p>
              This affected Spekit's customers, too. They would share feedback but didn't see their
              requests on the roadmap. They didn't know why their ideas weren't included and felt
              neglected. Overall, Andrea felt like they couldn't make decisions based on all the
              available data.
            </p>
            <p>
              Spekit set up Canny but didn't embrace it fully at first. For example, the team didn't
              realize they could vote on customers' behalf. Instead, they added comments with
              customer requests.
            </p>
            <p>That's where Andrea stepped in. She showed the team how to use Canny optimally.</p>

            <h2 className="sectionTitle">Solution</h2>
            <p>Andrea diligently organized all the feedback in Canny; she:</p>
            <ul>
              <li>Reviewed 650+ posts</li>
              <li>Marked 95 posts as "Complete"</li>
              <li>Cleaned up (rewrote or retagged) 100+ posts</li>
            </ul>
            <p className="quote">
              "It was exciting to mark so many posts as 'Complete.' It really showed how much we
              were listening to our customers. CSMs got really excited and motivated to continue
              putting all the feedback into Canny. Going through all the posts showed us how much
              our product has evolved."
            </p>
            <p>
              Now, Andrea can see all the data in one place and seamlessly connect it to Jira. Each
              feature request is clear and precise. Every Canny post has votes from interested
              users, comments with clarifications, and priority factors. Everything in progress
              links to corresponding Jira tickets for easy tracking.
            </p>
            <p>
              Andrea helped all teams use Canny efficiently. Now, CSMs vote on behalf of their
              customers, and the sales teams track missing features. When a particular feature goes
              live, sales can easily find which prospects requested it and follow up.
            </p>
            <p className="quote">
              "The sales team doesn't have to hunt down every prospect and review all previous
              calls. If any lead is blocked by a lack of specific functionality, the team records
              it. The R&D team also gets involved now &mdash; they go in and comment frequently.
              Sometimes, they say: 'This is an easy bug fix we can solve for the next release.'
              Overall, the whole team gets excited because we can easily see which feature is an
              easy fix and what's a heavier lift but a big win for the company."
            </p>
            <p>
              Instead of just guessing how many prospects or clients want a particular feature,
              Spekit can now rely on facts.
            </p>

            <h2 className="sectionTitle">Changelog</h2>
            <p>
              Andrea also owns Spekit's help center, and Canny's changelog helps her manage it. She
              writes release notes for every new feature and appreciates how simple it is with
              Canny.
            </p>
            <p className="quote">
              "When I saw the changelog functionality, I absolutely fell in love with it. I love the
              ability to search through the issues, categorize them, add dates, topics, etc. I moved
              all our release notes from Zendesk to Canny. Now, users who are interested in
              particular features can subscribe to updates about them. We can also link back from
              the changelog to the original post. Our CEO loves this functionality, too."
            </p>

            <h2 className="sectionTitle">Integrations</h2>
            <p>
              The team at Spekit also uses Canny's Slack integration. This integration sends Canny
              updates in a separate Slack channel &mdash; new posts, comments, upvotes, status
              changes, and more.
            </p>
            <p>
              The Slack integration helps Spekit stay connected, react quickly to new posts, and
              organize their feedback. Instead of switching between different tools, they can
              discuss each issue in Slack.
            </p>
            <p>
              Andrea also points out the Jira integration. It was instrumental in connecting their
              development team to the rest of the organization.
            </p>
            <p className="quote">
              "The Jira integration is a game-changer for us. When we mark a post as 'Complete' in
              Canny, it automatically gets tagged as 'Complete' in Jira too. This saves us time by
              eliminating this duplicative process of going back and forth between tools."
            </p>
            <p>
              Spekit also enjoys Canny's Autopilot &mdash; our latest AI feature. It opened up a new
              feedback channel for them &mdash; Zendesk.
            </p>
            <p className="quote">
              "We first tried Autopilot while it was in beta. We connected it to Zendesk, our
              primary support tool, and imported feature requests from there. I also love
              Autopilot's deduplicate feature &mdash; it saves us from a lot of manual work. We can
              easily merge and delete any posts."
            </p>

            <h2 className="sectionTitle">Results</h2>
            <p>
              Andrea uses Canny to track the progress of feature development. During monthly calls,
              they discuss every post that got marked as "Complete" and the engagement level of that
              feature. They also discuss any underutilized features in Canny and plan how to adopt
              them.
            </p>
            <p className="quote">
              "The team often asks for more features, and I get really excited because Canny often
              has them already. This week, the team wanted to assign priority to feature requests,
              and I said: 'Hey, Canny can do it, let me show you how!' The team loved it."
            </p>
            <p>Canny helps Andrea in every part of her job. She can:</p>
            <ol>
              <li>Import feature requests from various sources automatically</li>
              <li>Categorize, rank, and prioritize them</li>
              <li>Discuss each idea in Canny</li>
              <li>Add votes on behalf of users</li>
              <li>Connect each post to a Jira issue</li>
              <li>Get real-time updates in Slack</li>
              <li>Create a changelog entry every time a feature is live</li>
              <li>Automatically notify interested users about it</li>
            </ol>
            <p>The more Spekit uses Canny, the more features they want to explore.</p>
            <p className="quote">
              "I've been campaigning to add Canny's changelog widget to our app. I think it's an
              incredible feature. If someone is not using it yet, I'd want to know why. It saves me
              so much time."
            </p>
            <p>
              The most significant outcome of using Canny is being able to make data-backed
              decisions. Andrea says that her team feels very confident in the data Canny provides.
            </p>
            <p className="quote">
              "Having data from Canny brings the trust back into the conversation between R&D and
              GTM teams. Without it, we're just guessing what to&nbsp;build."
            </p>
            <p>
              We're happy to support Andrea and Spekit on this journey.{' '}
              <a href="https://canny.io/register" rel="noopener" target="_blank">
                Try Canny
              </a>{' '}
              yourself and see how much time you can save.
            </p>
          </div>
          <div className="right">
            <LazyLoadedImage
              alt="Spekit Company"
              className="companyLogo spekitWordmark"
              src={SpekitWordmark}
            />
            <p>
              Spekit is an AI-powered sales enablement platform. Their platform connects sales reps
              to the right content, like playbooks, case studies, and other helpful materials.
              Spekit helps sales reps succeed and win more deals.
            </p>
            <a href="https://www.spekit.com/" rel="noopener" target="_blank">
              spekit.com
            </a>
            <div className="stat">
              <UppercaseHeader>Employees</UppercaseHeader>
              <p>80</p>
            </div>
            <div className="stat">
              <UppercaseHeader>Founded</UppercaseHeader>
              <p>2019</p>
            </div>
            <div className="stat">
              <UppercaseHeader>Using Canny since</UppercaseHeader>
              <p>2021</p>
            </div>
            <div className="stat">
              <UppercaseHeader>Votes on Canny</UppercaseHeader>
              <p>1,381</p>
            </div>
          </div>
        </div>
        <LandingFooterCTA style="solid" expanded={true} />
      </div>
    </div>
  );
};

export default SpekitLanding;
